import { useState } from "react";
import { useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import type { GetInactiveStoresQuery } from "../../../__generated__/graphql";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

const GET_INACTIVE_STORES = gql(`
  query GetInactiveStores {
    inactiveStores {
      id
      title
      img
      address
      createdAt
      updatedAt
      contactNumber
      totalSales
      seller {
        contactNumber
      }
      products {
        id
        createdAt
      }
    }
  }
`);

type StoreRow = NonNullable<GetInactiveStoresQuery["inactiveStores"]>[number];

function getLastProductDate(store: StoreRow): string {
  if (!store.products?.length) return "-";
  const sorted = [...store.products].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  return new Date(sorted[0].createdAt).toLocaleDateString("ko-KR");
}

export default function AdminInactiveStores() {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data, loading, error } =
    useQuery<GetInactiveStoresQuery>(GET_INACTIVE_STORES);

  const fullList = data?.inactiveStores ?? [];
  const keyword = searchKeyword.trim().toLowerCase();
  const list = keyword
    ? fullList.filter(
        (store) =>
          store.title.toLowerCase().includes(keyword) ||
          (store.address?.toLowerCase().includes(keyword) ?? false),
      )
    : fullList;

  const downloadExcel = () => {
    if (fullList.length === 0) {
      alert("다운로드할 데이터가 없습니다.");
      return;
    }

    const headers = [
      "매장 ID",
      "매장명",
      "주소",
      "연락처",
      "생성일",
      "마지막 실시간 판매 업로드일",
      "총 매출",
    ];

    const rows = fullList.map((store) => [
      store.id.toString(),
      store.title || "",
      store.address || "주소 정보 없음",
      store.seller?.contactNumber || store.contactNumber || "연락처 정보 없음",
      new Date(store.createdAt).toLocaleDateString("ko-KR"),
      getLastProductDate(store),
      (store.totalSales || 0).toLocaleString("ko-KR"),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell || "");
            if (
              cellStr.includes(",") ||
              cellStr.includes('"') ||
              cellStr.includes("\n")
            ) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(","),
      ),
    ].join("\n");

    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const fileName = `비활성화_매장_${new Date().toISOString().split("T")[0]}.csv`;
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="flex justify-center py-8">
          <LoadingSpinnerInline />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-center py-8 text-red-600">
          에러가 발생했습니다: {error.message}
        </div>
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="비활성화 매장 조회"
      description={`비활성화된 매장 목록을 확인할 수 있습니다. (총 ${fullList.length}개)`}
      headerExtra={
        <div className="space-y-4">
          <div className="flex justify-end">
            {fullList.length > 0 && (
              <button
                type="button"
                onClick={downloadExcel}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                엑셀 다운로드
              </button>
            )}
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h2 className="text-sm font-semibold text-blue-900 mb-2">
              비활성화 매장 판단 기준
            </h2>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>예약날짜를 모두 닫은 경우</li>
              <li>마지막 실시간 판매 업로드일이 3주 전인 경우</li>
            </ul>
          </div>
        </div>
      }
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명·주소로 검색",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={list.map((s) => ({
              id: s.id,
              title: s.title,
              img: s.img,
              address: s.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={(store) =>
              setSelectedStore(list.find((x) => x.id === store.id) ?? null)
            }
            extraHeaders={
              <th className="px-4 py-2 font-medium text-gray-700">
                총 매출
              </th>
            }
            renderExtraCells={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (!full) return null;
              return (
                <td className="px-4 py-3 text-gray-600 text-xs">
                  {(full.totalSales || 0).toLocaleString("ko-KR")}원
                </td>
              );
            }}
            renderExtraCard={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (!full) return null;
              return (
                <p className="text-xs text-gray-600 mt-1">
                  총 매출: {(full.totalSales || 0).toLocaleString("ko-KR")}원
                </p>
              );
            }}
            emptyText={
              keyword
                ? "검색 결과가 없습니다."
                : "비활성화된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "매장 상세",
        children: selectedStore ? (
          <div className="space-y-4 text-sm">
            <div>
              <span className="font-medium text-gray-800">선택 매장:</span>{" "}
              {selectedStore.title} (ID: {selectedStore.id})
            </div>

            <div className="flex items-center gap-3">
              {selectedStore.img ? (
                <ResponsiveCDNImage
                  src={selectedStore.img}
                  alt={selectedStore.title}
                  className="w-20 h-20 rounded object-cover"
                  sizeRatio={0.2}
                />
              ) : (
                <div className="w-20 h-20 bg-gray-200 rounded" />
              )}
              <div>
                <div className="font-medium text-gray-800">
                  {selectedStore.title}
                </div>
                <div className="text-gray-600">
                  {selectedStore.address ?? "—"}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded space-y-2 text-gray-700">
              <div>
                <span className="font-medium text-gray-800">연락처:</span>{" "}
                {selectedStore.seller?.contactNumber ||
                  selectedStore.contactNumber ||
                  "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">생성일:</span>{" "}
                {new Date(selectedStore.createdAt).toLocaleDateString("ko-KR")}
              </div>
              <div>
                <span className="font-medium text-gray-800">
                  마지막 실시간 판매 업로드일:
                </span>{" "}
                {getLastProductDate(selectedStore)}
              </div>
              <div>
                <span className="font-medium text-gray-800">총 매출:</span>{" "}
                {(selectedStore.totalSales || 0).toLocaleString("ko-KR")}원
              </div>
            </div>
          </div>
        ) : null,
      }}
    />
  );
}
